/**
 * Update card & retry (gdn-3064): recover a declined card-on-file charge.
 *
 * The verb leads with card selection, deliberately: a bare "Retry" against
 * a card the issuer has already refused — production shows declines
 * sitting at nine attempts on a single card — fails again, and repeated
 * authorisations against a failing card are what issuers penalise. So the
 * biller picks the card first, and nothing is preselected: an explicit
 * choice is the point of the dialog.
 *
 * The issuer's decline reason renders verbatim ("Your card has
 * insufficient funds." beats any paraphrase; the decline-code set is not
 * ours to enumerate) so the biller can tell a replace-the-card decline
 * from a call-the-bank one.
 *
 * The retry itself is injected (`onRetry`): the POST
 * /v2/provider/invoices/{id}/retry-payment endpoint ships with gdn-3061
 * and the surface wires it in once it is in the deployed schema. NOTE for
 * that wiring: the response's `payment_failure_count` /
 * `last_payment_failure_reason` are written by the async webhook and can
 * still predate the attempt that just happened — read `status` and
 * `message` for the outcome of the click, and treat those two as history.
 */

import React, { useState } from 'react';
import { useGetPatientPaymentMethodsQuery } from '@features/payments/store/api';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';

import { declineNoteDetail, formatCents, type RowInvoiceDecline } from './rows';

export interface UpdateCardAndRetryDialogProps {
  patientId: string;
  /** The decline being recovered — its verbatim reason heads the dialog. */
  decline: RowInvoiceDecline;
  /** The invoice amount, when the capped history carried the record. */
  amountCents?: number | null;
  onClose: () => void;
  /** Fired at confirm-click (the interaction, for analytics), before the
   * POST. `payment_method_changed` is true when the chosen card is not
   * the current default — i.e. the biller actually updated, not re-ran. */
  onSubmitted?: (payload: {
    payment_method_id: string;
    payment_method_changed: boolean;
  }) => void;
  /** Runs the retry against the chosen card and resolves when the server
   * accepts it. Reject (with a message) to keep the dialog open showing
   * the error. See the module note about reading the response. */
  onRetry: (paymentMethodId: string) => Promise<void>;
}

export const UpdateCardAndRetryDialog: React.FC<UpdateCardAndRetryDialogProps> = ({
  patientId,
  decline,
  amountCents,
  onClose,
  onSubmitted,
  onRetry,
}) => {
  const { data, isLoading, isError, refetch } = useGetPatientPaymentMethodsQuery({
    patientId,
  });
  const methods = (data?.payment_methods ?? []).filter(
    method => method.card_last4,
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const selected = methods.find(method => method.id === selectedId);

  const handleConfirm = async () => {
    if (!selected) return;
    onSubmitted?.({
      payment_method_id: selected.id,
      payment_method_changed: !selected.is_default,
    });
    setIsSubmitting(true);
    setErrorText(null);
    try {
      await onRetry(selected.id);
      onClose();
    } catch (error) {
      setErrorText(
        error instanceof Error && error.message
          ? error.message
          : 'The retry could not be started. Nothing was charged — try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={isSubmitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
        Update card &amp; retry
        {amountCents != null ? ` — ${formatCents(amountCents)}` : ''}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {declineNoteDetail(decline)}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Charge which card?
        </Typography>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : isError ? (
          // A failed lookup is not an empty wallet: saying "no saved
          // cards" here would send the biller off to add a card that may
          // already exist.
          <Alert
            severity="error"
            sx={{ fontSize: '0.8125rem' }}
            action={
              <Button
                size="small"
                onClick={() => void refetch()}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Try again
              </Button>
            }
          >
            Couldn&apos;t load the saved cards.
          </Alert>
        ) : methods.length === 0 ? (
          <Alert severity="info" sx={{ fontSize: '0.8125rem' }}>
            No saved cards. Add a card from the payment method chip in the
            header, then retry from here.
          </Alert>
        ) : (
          <RadioGroup
            value={selectedId ?? ''}
            onChange={event => setSelectedId(event.target.value)}
          >
            {methods.map(method => (
              <FormControlLabel
                key={method.id}
                value={method.id}
                control={<Radio size="small" />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      {(method.card_brand ?? 'Card').replace(/^./, c =>
                        c.toUpperCase(),
                      )}{' '}
                      •••• {method.card_last4}
                      {method.card_exp_month && method.card_exp_year
                        ? ` — expires ${method.card_exp_month}/${method.card_exp_year}`
                        : ''}
                    </Typography>
                    {method.is_default && (
                      // The declined attempts ran against the card on
                      // file — flag it so "pick the same card again" is a
                      // choice, not an accident.
                      <Chip
                        label="current card — the one declining"
                        size="small"
                        sx={{ height: 18, fontSize: '0.625rem', fontWeight: 600 }}
                      />
                    )}
                  </Box>
                }
              />
            ))}
          </RadioGroup>
        )}
        {errorText && (
          <Alert severity="error" sx={{ fontSize: '0.8125rem' }}>
            {errorText}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selected || isSubmitting}
          onClick={() => void handleConfirm()}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {isSubmitting ? 'Retrying…' : 'Retry payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
